import { useMemo } from 'react';
import { useIsElectron } from '@/context/ElectronContext';
import { getVisibleActions } from '@/common/types/itemActions';
import { resolveLocalPath } from '@/common/utils/localPathUtils';
import type { FileNode } from '@/common/utils/fileTreeUtils';
import { useRevealInFileExplorer } from './useRevealInFileExplorer';

interface UseFileTreeItemActionsProps {
  node: FileNode;
  projectDirPath?: string | null;
}

export function useFileTreeItemActions({
  node,
  projectDirPath,
}: UseFileTreeItemActionsProps) {
  const isElectron = useIsElectron();
  const { revealInExplorer } = useRevealInFileExplorer();
  const localPath = resolveLocalPath(projectDirPath ?? null, node.path);

  return useMemo(
    () =>
      getVisibleActions([
        {
          id: node.type === 'folder' ? 'open-folder' : 'open-file-location',
          label: node.type === 'folder' ? 'Open folder' : 'Open file location',
          visible: isElectron && !!localPath,
          onClick: () => {
            if (!localPath) {
              return;
            }

            revealInExplorer(
              {
                path: localPath,
                kind: node.type === 'folder' ? 'folder' : 'file',
              },
              node.type === 'folder'
                ? 'Opening folder...'
                : 'Opening file location...'
            );
          },
        },
      ]),
    [isElectron, localPath, node.type, revealInExplorer]
  );
}
